import { zodResolver } from "@hookform/resolvers/zod";
import { getLocalTimeZone } from "@internationalized/date";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";

import { editShipping } from "@blis/shared/schemas/accession";
import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { DatePickerInput } from "@/components/ui/date-picker";
import {
  DatePickerTimeZone,
  DatePickerWithZone,
} from "@/components/ui/date-picker-with-zone";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, Label, TextField } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import {
  NonFocusableTooltipTarget,
  Tooltip,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LookupCacheProvider } from "@/lib/cache";
import type { TAccessionDetail } from "@/lib/types";
import { resolveReceiveTimezoneFromLabId } from "../../lib/receive-timezone";
import { parseZonedDateTime } from "../detail/SampleSection";
import { useDialogProps } from "../detail/state";
import { CaseTypeSelect } from "../form-new/components/CaseTypeSelect";
import { ReceivingOrgSelect } from "../form-new/components/ReceivingOrgSelect";

type FormValues = z.infer<typeof editShipping>;

export function UpdateAdditionalDialog({
  accession,
}: {
  accession: TAccessionDetail;
}) {
  const dialogProps = useDialogProps("editAdditional");
  const { data: primaryLabs = [] } = trpc.orgs.primaryLabs.useQuery(undefined, {
    enabled: dialogProps.open,
  });

  const update = trpc.accession.editShipping.useMutation({
    onSuccess() {
      toast.success("Accession updated");
      dialogProps.onOpenChange?.(false);
    },
    onError(err) {
      toast.error("Error updating accession", {
        description: err.message,
      });
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      accessionId: accession.id,
      caseType: accession.caseType ?? null,
      receivingOrgId: accession.receivingOrg?.id,
      packedAt: accession.packedAt?.toISOString() ?? null,
      packedTimezone: accession.packedTimezone ?? null,
      trackingId: accession.trackingId ?? "",
      requisitionId: accession.requisitionId ?? "",
      receivedAt: accession.receivedAt ?? null,
    },
    resolver: zodResolver(editShipping),
  });

  const onSubmit = async (value: FormValues) => {
    await update.mutateAsync({
      ...value,
      ...(value.packedAt ? {} : { packedTimezone: null }),
    });
  };

  const packedTimezoneValue = watch("packedTimezone") ?? getLocalTimeZone();
  const receivingOrgId = watch("receivingOrgId");
  const receiveTimezoneInfo = useMemo(
    () =>
      resolveReceiveTimezoneFromLabId(
        receivingOrgId,
        primaryLabs,
        accession.receivingOrg,
      ),
    [receivingOrgId, primaryLabs, accession.receivingOrg],
  );

  return (
    <Modal isOpen={dialogProps.open} onOpenChange={dialogProps.onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Edit Accession</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <LookupCacheProvider>
            <form
              id="update-samples-form"
              onSubmit={(evt) => {
                evt.preventDefault();
                handleSubmit(onSubmit)(evt);
              }}
            >
              <div className="flex flex-col gap-4">
                <Controller
                  control={control}
                  name="caseType"
                  render={({ field, fieldState: { error } }) => (
                    <CaseTypeSelect
                      value={field.value}
                      onChange={field.onChange}
                      label="Case type"
                      isInvalid={!!error}
                      error={error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="receivingOrgId"
                  render={({ field, fieldState: { error } }) => (
                    <ReceivingOrgSelect
                      value={field.value}
                      onChange={field.onChange}
                      isInvalid={!!error}
                      error={error?.message ?? ""}
                      label="Receiving lab"
                    />
                  )}
                />
                {accession.received ? (
                  <Controller
                    control={control}
                    name="receivedAt"
                    render={({ field, fieldState: { error } }) => (
                      <div className="flex flex-col gap-1">
                        <DatePickerWithZone
                          name={field.name}
                          aria-label="Received time"
                          className="min-w-48 block"
                          granularity="minute"
                          selectableTimezones={[receiveTimezoneInfo.timezone]}
                          value={
                            field.value instanceof Date
                              ? parseZonedDateTime(
                                  field.value.toISOString(),
                                  receiveTimezoneInfo.timezone,
                                )
                              : null
                          }
                          onChange={(val) => {
                            if (val) {
                              field.onChange(val.toDate());
                            }
                          }}
                          onBlur={field.onBlur}
                          isInvalid={!!error}
                          validationBehavior="aria"
                        >
                          <Label>Received time</Label>
                          <div className="flex flex-row items-center gap-1">
                            <DatePickerInput />
                            <TooltipTrigger delay={300}>
                              <NonFocusableTooltipTarget>
                                <span className="inline-flex">
                                  <DatePickerTimeZone isDisabled />
                                </span>
                              </NonFocusableTooltipTarget>
                              <Tooltip>
                                {receiveTimezoneInfo.missingConfiguredTimezone
                                  ? "Receiving lab time zone is not configured. Set it in org settings for the receiving lab."
                                  : "Received time uses the receiving lab's time zone. Change it in org settings for that lab."}
                              </Tooltip>
                            </TooltipTrigger>
                          </div>
                        </DatePickerWithZone>
                        {receiveTimezoneInfo.missingConfiguredTimezone ? (
                          <Text size="1" muted>
                            Receiving lab timezone not configured
                          </Text>
                        ) : null}
                      </div>
                    )}
                  />
                ) : null}
                <Controller
                  control={control}
                  name="packedAt"
                  render={({ field, fieldState: { error } }) => (
                    <DatePickerWithZone
                      name={field.name}
                      aria-label="Packing time"
                      className="min-w-48 block"
                      granularity="minute"
                      value={
                        field.value
                          ? parseZonedDateTime(field.value, packedTimezoneValue)
                          : null
                      }
                      onChange={(val) => {
                        if (val) {
                          field.onChange(val.toAbsoluteString());
                          setValue("packedTimezone", val.timeZone);
                        } else {
                          field.onChange(null);
                          setValue("packedTimezone", null);
                        }
                      }}
                      onBlur={field.onBlur}
                      isInvalid={!!error}
                      validationBehavior="aria"
                    >
                      <Label>Packing time</Label>
                      <div className="flex flex-row items-center gap-1">
                        <DatePickerInput />
                        <DatePickerTimeZone />
                      </div>
                    </DatePickerWithZone>
                  )}
                />
                <Controller
                  control={control}
                  name="trackingId"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      type="text"
                      id="trackingId"
                      aria-label="Tracking #"
                      className="w-full"
                      value={field.value ?? ""}
                      onChange={(val) => field.onChange(val ?? null)}
                      onBlur={field.onBlur}
                      name={field.name}
                      isInvalid={!!error}
                      validationBehavior="aria"
                    >
                      <Label>Tracking #</Label>
                      <Input placeholder="Tracking ID" className="max-w-58" />
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name="requisitionId"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      type="text"
                      id="requisitionId"
                      aria-label="Requisition ID"
                      className="w-full"
                      value={field.value ?? ""}
                      onChange={(val) => field.onChange(val ?? null)}
                      onBlur={field.onBlur}
                      name={field.name}
                      isInvalid={!!error}
                      validationBehavior="aria"
                    >
                      <Label>Requisition ID</Label>
                      <Input
                        placeholder="Requisition ID"
                        className="max-w-58"
                      />
                    </TextField>
                  )}
                />
              </div>
            </form>
          </LookupCacheProvider>
        </DialogBody>
        <DialogFooter>
          <Button
            type="submit"
            form="update-samples-form"
            isPending={isSubmitting}
          >
            Save
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
